import { Icon } from '@iconify/react';
import { Button, Spinner, Switch, Tooltip } from '@nextui-org/react';
import { useQuery } from '@tanstack/react-query';
import { useCreateOrUpdatePrompt, useDeletePrompt } from 'Queries/prompts';
import React from 'react';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';
import AnalyticsModal from '../../components/analytics-modal';
import DataView from '../../components/data-view/data-view';
import ListCard from '../../components/list-card';
import DeleteConfirmationModal from '../../components/modal/DeleteConfirmationModal';
import { TableConfig } from '../../components/table/types';
import { useGenerateAgentAnalyticsReport } from '../../hooks/queries/export';
import PromptService from '../../services/prompt-service';
import { useLoaderStore } from '../../store/use-loader-store';
import { PromptData, StandardResponse } from '../../types/agent';
import { promptToPlainText } from '../../utils/promptUtils';

export default function ListAgent() {
  const navigate = useNavigate();
  const [viewType, setViewType] = React.useState<'tile' | 'list'>('tile');
  const [deletingPromptId, setDeletingPromptId] = React.useState<number | null>(
    null
  );
  const setIsLoading = useLoaderStore((state) => state.setIsLoading);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState(false);
  const [selectedPromptToDelete, setSelectedPromptToDelete] = React.useState<
    number | null
  >(null);
  const [isAnalyticsModalOpen, setIsAnalyticsModalOpen] = React.useState(false);
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const generateAgentAnalytics = useGenerateAgentAnalyticsReport();

  const {
    data,
    isLoading,
    isError,
    refetch: refetchPrompts,
  } = useQuery<StandardResponse<PromptData>>({
    queryKey: ['agents'],
    queryFn: async (): Promise<StandardResponse<PromptData>> => {
      const response = await PromptService.getPrompts({
        filters: {
          page: 1,
        },
      });
      return response as StandardResponse<PromptData>;
    },
  });

  const deletePromptMutation = useDeletePrompt();
  const updatePromptMutation = useCreateOrUpdatePrompt({
    doNotRedirect: true,
    successMessage: 'Prompt status updated successfully',
  });

  const handleDelete = (promptId: number) => {
    setSelectedPromptToDelete(promptId);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = () => {
    if (selectedPromptToDelete) {
      setDeletingPromptId(selectedPromptToDelete);
      deletePromptMutation.mutate(selectedPromptToDelete, {
        onSuccess: () => {
          toast.success('Prompt deleted successfully');
          refetchPrompts();
          setDeletingPromptId(null);
          setIsDeleteModalOpen(false);
          setSelectedPromptToDelete(null);
        },
        onError: (error) => {
          toast.error(
            `Failed to delete prompt ${error?.message ? error.message : ''}`
          );
          setDeletingPromptId(null);
          setIsDeleteModalOpen(false);
          setSelectedPromptToDelete(null);
        },
      });
    }
  };

  const handleToggle = (id: number, currentStatus: boolean) => {
    setIsLoading(true);
    updatePromptMutation.mutate(
      {
        id: id.toString(),
        doNotRedirect: false,
        data: { is_active: !currentStatus },
      },
      {
        onSuccess: () => {
          refetchPrompts();
          setIsLoading(false);
        },
        onError: () => {
          setIsLoading(false);
        },
      }
    );
  };

  const handleGenerateAnalytics = (filters: any) => {
    generateAgentAnalytics.mutate(filters, {
      onSuccess: () => {
        setIsAnalyticsModalOpen(false);
      },
    });
  };

  const renderCard = (agent: PromptData) => (
    <ListCard
      key={agent.id}
      title={agent.name}
      description={promptToPlainText(agent.system_prompt ?? '')}
      onEdit={() => navigate(`/${tenantSlug}/agents/${agent.id}/edit`)}
      onDelete={() => handleDelete(agent.id)}
      isDeleting={deletingPromptId === agent.id}
      subtitle={`Created by ${agent.created_by.username} on ${new Date(
        agent.created_at
      ).toLocaleString()}`}
      actions={
        <div className="flex gap-2">
          <Tooltip
            content={agent.is_active ? 'Deactivate agent' : 'Activate agent'}
            placement="top"
          >
            <span className="inline-flex items-center">
              <Switch
                size="sm"
                className="scale-75"
                isSelected={agent.is_active}
                onValueChange={() => handleToggle(agent.id, agent.is_active)}
                aria-label="Toggle active status"
              />
            </span>
          </Tooltip>
          <Tooltip content="Visualize">
            <Button
              size="sm"
              isIconOnly
              variant="light"
              onPress={() =>
                navigate(`/${tenantSlug}/agents/${agent.id}/visualize`, {
                  state: { agentName: agent.name, agentId: agent.id },
                })
              }
            >
              <Icon icon="hugeicons:flow" width="20" />
            </Button>
          </Tooltip>
        </div>
      }
    />
  );

  const agentTableConfig: TableConfig = {
    // Size columns to their content: Name and System Prompt wrap and take the
    // remaining room, Status / Actions stay as narrow as their controls.
    layout: 'auto',
    columns: [
      {
        uid: 'name',
        name: 'Name',
        sortable: true,
        wrap: true,
        // Floor for content sizing, so long names don't get squeezed.
        renderCell: (agent: PromptData) => (
          <div className="min-w-[240px]">{agent.name}</div>
        ),
      },
      {
        uid: 'system_prompt',
        name: 'System Prompt',
        sortable: true,
        wrap: true,
        renderCell: (agent: PromptData) => (
          <div
            className="line-clamp-2"
            title={promptToPlainText(agent.system_prompt ?? '')}
          >
            {promptToPlainText(agent.system_prompt ?? '')}
          </div>
        ),
      },
      {
        uid: 'created_by',
        name: 'Created By',
        renderCell: (agent: PromptData) => (
          <span>{agent.created_by.username}</span>
        ),
      },
      {
        uid: 'created_at',
        name: 'Created At',
        renderCell: (agent: PromptData) => (
          <span>{new Date(agent.created_at).toLocaleString()}</span>
        ),
      },
      {
        uid: 'updated_at',
        name: 'Last Updated At',
        renderCell: (agent: PromptData) => (
          <span>{new Date(agent.updated_at).toLocaleString()}</span>
        ),
      },
      {
        uid: 'is_active',
        name: 'Status',
        renderCell: (agent: PromptData) => (
          <Tooltip
            content={agent.is_active ? 'Deactivate agent' : 'Activate agent'}
            placement="top"
          >
            <span className="inline-flex items-center">
              <Switch
                size="sm"
                className="scale-75"
                isSelected={agent.is_active}
                onValueChange={() => handleToggle(agent.id, agent.is_active)}
                aria-label="Toggle active status"
              />
            </span>
          </Tooltip>
        ),
      },
      {
        uid: 'actions',
        name: 'Actions',
        align: 'end',
        renderCell: (agent: PromptData) => (
          <div className="flex items-center justify-end gap-2">
            <Tooltip content="Edit">
              <Button
                isIconOnly
                variant="light"
                size="sm"
                onClick={() =>
                  navigate(`/${tenantSlug}/agents/${agent.id}/edit`)
                }
                aria-label="Edit"
              >
                <Icon icon="akar-icons:edit" width="20" />
              </Button>
            </Tooltip>
            <Tooltip content="Delete">
              <Button
                isIconOnly
                variant="light"
                size="sm"
                color="danger"
                onClick={() => handleDelete(agent.id)}
                isDisabled={deletingPromptId === agent.id}
                aria-label="Delete"
              >
                {deletingPromptId === agent.id ? (
                  <Icon
                    icon="lucide:loader-circle"
                    width="20"
                    className="animate-spin"
                  />
                ) : (
                  <Icon icon="lucide:trash-2" width="20" />
                )}
              </Button>
            </Tooltip>
          </div>
        ),
      },
    ],
    initialVisibleColumns: [
      'name',
      'system_prompt',
      'created_by',
      'created_at',
      'is_active',
      'actions',
    ],
    searchable: true,
    searchPlaceholder: 'Search agents...',
    searchKey: 'name',

    pagination: {
      enabled: true,
      initialRowsPerPage: 100,
      rowsPerPageOptions: [],
      showControls: true,
      showShadow: true,
    },

    actions: {
      enableAdd: false,
      addButtonLabel: 'Add Agent',
      onAdd: () => navigate(`/${tenantSlug}/agents/create`),
    },

    title: 'Agents',
    description: 'Manage AI agents',
    disableDefaultSort: true,
    stickyHeader: true,
    maxHeight: '700px',
    className: '',
  };

  const actions = (
    <div className="flex gap-2">
      <Button
        color="primary"
        variant="flat"
        onPress={() => setIsAnalyticsModalOpen(true)}
        startContent={<Icon icon="heroicons:chart-bar" width="20" />}
        isLoading={generateAgentAnalytics.isPending}
      >
        Agent Analytics
      </Button>

      <Button
        color="primary"
        onPress={() => navigate(`/${tenantSlug}/agents/create`)}
        startContent={<Icon icon="mdi:plus" />}
      >
        Add Agent
      </Button>
    </div>
  );

  if (isLoading)
    return (
      <div className="flex h-full min-h-0 flex-1 items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  if (isError)
    return (
      <div className="text-center text-danger">
        Error loading agents. Please try again later.
      </div>
    );
  // if (!data?.results?.length)
  //   return <div className="text-center">No agents to display</div>;

  return (
    <>
      <DataView<PromptData>
        title="Agents"
        actions={actions}
        data={data?.results || []}
        tableConfig={agentTableConfig}
        viewType={viewType}
        onViewChange={setViewType}
        renderCard={renderCard}
      />
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        title="Delete Agent"
        entityName="agent"
        isLoading={deletingPromptId === selectedPromptToDelete}
      />
      <AnalyticsModal
        isOpen={isAnalyticsModalOpen}
        onClose={() => setIsAnalyticsModalOpen(false)}
        onGenerate={handleGenerateAnalytics}
        isLoading={generateAgentAnalytics.isPending}
        type="agent-analytics"
      />
    </>
  );
}
